import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import type { LabTestGroupKeys } from '~/components/common/lab-orders/labTestUtils'
import FeatureToggle from '~/constants/featureToggle'
import LabVendorNames from '~/constants/labVendorName'
import { LabTestState } from '~/constants/SOAPStates'
import { useGetLabTestStateId } from '~/store/hooks/orders'
import {
  getBreed,
  getFeatureToggle,
  getLabOrderStatuses,
  getLabVendors,
} from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import type { LabOrder, Order } from '~/types'
import { getFullBreedName } from '~/utils'

import FinalizeOrdersRow from '../FinalizeOrdersRow'
import { FinalizeLabOrdersActions } from './FinalizeLabOrdersActions'

const useStyles = makeStyles(
  (theme) => ({
    updateLink: {
      marginLeft: theme.spacing(1),
      color: theme.palette.common.white,
      textDecoration: 'underline',
      fontWeight: 500,
    },
    alert: {
      borderRadius: 0,
      margin: theme.spacing(1, 0, 0, 0),
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      fontSize: '1.2rem',
      padding: theme.spacing(0.5, 1),
      display: 'flex',
      alignItems: 'center',
    },
  }),
  { name: 'FinalizeLabOrdersRow' },
)

interface FinalizeLabOrdersRowProps {
  clientId: string | null | undefined
  currentOrderLabTestMap: Record<string, Record<string, Order[]>>
  handleCancel: () => void
  handleEdit: () => void
  handlePrintLabel: () => void
  hasIntegrationMap: Record<string, boolean>
  keysToPick: LabTestGroupKeys[]
  order: LabOrder
  patientId: string | null | undefined
}

const FinalizeLabOrdersRow: React.FC<FinalizeLabOrdersRowProps> = ({
  clientId,
  patientId,
  order,
  currentOrderLabTestMap,
  keysToPick,
  hasIntegrationMap,
  handleEdit,
  handlePrintLabel,
  handleCancel,
}) => {
  const classes = useStyles()
  const { t } = useTranslation('Common')
  const [searchParams] = useSearchParams()
  const businessId = searchParams.get('businessId')

  const LabOrdersStatusesList = useSelector(getLabOrderStatuses)
  const LabVendors = useSelector(getLabVendors)
  const getLabTestStateId = useGetLabTestStateId()
  const isBreedWarningEnabled = useSelector(
    getFeatureToggle(FeatureToggle.BREED_UNKNOWN_LAB_WARNING),
  )

  const { id: orderId, labOrderId, statusId, vendorId } = order

  const getOrderLabTests = () => {
    if (!currentOrderLabTestMap[vendorId]) {
      return []
    }

    if (orderId) {
      return R.pathOr([], [vendorId, orderId], currentOrderLabTestMap)
    }

    return R.pipe(
      R.values,
      R.flatten,
    )(R.pick(keysToPick, currentOrderLabTestMap[vendorId]))
  }
  const orderLabTests: Order[] = getOrderLabTests()
  const vendorName = LanguageUtils.getConstantTranslatedName(
    vendorId,
    LabVendors,
  )
  const orderStatus = LanguageUtils.getConstantTranslatedName(
    statusId,
    LabOrdersStatusesList,
  )

  const hasIntegration = hasIntegrationMap[vendorId]
  const isInternalVendor = vendorName === LabVendorNames.INTERNAL

  const declineLabel = `(${t('Common:DECLINED')})`
  const orderNameDetails =
    orderLabTests.length > 0
      ? orderLabTests.map((orderLabTest: Order) => {
          if (orderLabTest.stateId === getLabTestStateId(LabTestState.DECLINED))
            return {
              orderName: `${orderLabTest.name} ${declineLabel}`,
              softText: true,
            }
          return { orderName: orderLabTest.name, softText: false }
        })
      : undefined

  const patient = useSelector(getPatient(patientId))
  const BreedConstants = useSelector(getBreed)
  const fullBreed = patient?.breeds?.length
    ? getFullBreedName(patient.species, patient.breeds, BreedConstants)
    : ''
  const normalized = (fullBreed || '').trim().toLowerCase()
  const showBreedWarning =
    isBreedWarningEnabled && (!normalized || normalized === 'unknown')
  const profileUrl: string | undefined =
    clientId && patientId
      ? (() => {
          const baseUrl = `/client/${clientId}/patient/${patientId}`
          const businessIdParam = businessId
            ? `businessId=${encodeURIComponent(businessId)}&`
            : ''
          const queryString = `?${businessIdParam}tab=profile`
          return baseUrl + queryString
        })()
      : undefined

  const breedWarningContent = showBreedWarning ? (
    <Alert className={classes.alert} severity="error" variant="filled">
      {t('BREED_WARNING_MESSAGE')}{' '}
      <a
        className={classes.updateLink}
        href={profileUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        {t('BREED_WARNING_LINK_TEXT')}
      </a>
    </Alert>
  ) : undefined

  return (
    <FinalizeOrdersRow
      actions={
        <FinalizeLabOrdersActions
          handleCancel={handleCancel}
          handleEdit={handleEdit}
          handlePrintLabel={handlePrintLabel}
          order={order}
          orderLabTests={orderLabTests}
          vendorId={vendorId}
        />
      }
      breedWarningContent={breedWarningContent}
      labOrderId={labOrderId}
      orderNameDetails={orderNameDetails}
      showWarning={!hasIntegration && !isInternalVendor}
      statusName={orderStatus}
      vendorName={vendorName}
    />
  )
}

export default FinalizeLabOrdersRow
